import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Environment } from '../classes/environment';
import { base64ToDataUrl } from '../utils/base64-data-url';

export interface WebBenutzerProfilbild {
  ebeId: string;                         
  ebeProfilbild?: string | null;         
  ebeProfilbildName?: string | null;     
  ebeProfilbildMini?: string | null;     
  mime?: string | null;                  
}

/**
 * BenutzerProfilbildService
 * - GET full: returns the full-size avatar as Blob (null on 204/errors)
 * - PUT upload: uploads/replaces the avatar image
 * - DELETE: removes user's avatar
 * - POST listMini: fetches mini avatars for multiple ebeIds and returns a Map of ebeId -> dataURL | null
 */
@Injectable({ providedIn: 'root' })
export class UserProfileImageService {

  constructor(
    @Inject('environment') private environment: Environment,
    private http: HttpClient,
  ) {}

  /**
   * Fetch full profile image as Blob. Returns null on 204 or errors.
   * Backend: GET /benutzer/profilbild/{ebeId}
   */
  getFull(ebeId: string, opts?: { cacheBust?: boolean }): Observable<Blob | null> {

    const url = `${this.environment.backendUrl}benutzer/profilbild/${ebeId}`;
    let params = new HttpParams();
    if (opts?.cacheBust) params = params.set('cb', Date.now().toString());

    return this.http.get(url, {
      responseType: 'blob',
      observe: 'response',
      params,
      withCredentials: true,
    }).pipe(
      map(resp => {
        if (!resp || resp.status === 204) return null;
        return resp.body as Blob;
      }),
      catchError((_e: HttpErrorResponse) => of(null))
    );
  }
  /**
   * Upload profile image. Returns true on success, false on error.
   * Backend: PUT /benutzer/profilbild/upload (multipart/form-data: ebeId, file)
   */
  upload(ebeId: string, file: File | Blob): Observable<boolean> {
    const url = `${this.environment.backendUrl}benutzer/profilbild/upload`;
    const form = new FormData();
    form.append('ebeId', ebeId);
    form.append('file', file);
    return this.http.put<{ verarbeitet?: boolean }>(url, form).pipe(
      map(res => !!res && (res as any).verarbeitet !== false),
      catchError((_e: HttpErrorResponse) => of(false))
    );
  }

  /**
   * Remove profile image. Returns true on success, false on error.
   * Backend: DELETE /benutzer/profilbild/delete?ebeId=...
   */
  remove(ebeId: string): Observable<boolean> {
    const url = `${this.environment.backendUrl}benutzer/profilbild/delete`;
    const params = new HttpParams().set('ebeId', ebeId);
    return this.http.delete<{ verarbeitet?: boolean }>(url, { params }).pipe(
      map(res => !!res && (res as any).verarbeitet !== false),
      catchError((_e: HttpErrorResponse) => of(false))
    );
  }

	/**
	 * listMini: fetch mini avatars for multiple users.
	 * Returns a Map<ebeId, dataURL|null>. 204/errors map to null.
	 * Backend: POST /benutzer/profilbild/list
	 */
	listMini(ebeIds: string[]): Observable<Map<string, string | null>> {
		if (!ebeIds || !ebeIds.length) return of(new Map());
		const url = `${this.environment.backendUrl}benutzer/profilbild/list`;
		return this.http.post<WebBenutzerProfilbild[] | any>(url, ebeIds, { withCredentials: true } ).pipe(
			map(payload => this.normalizeMiniResponse(payload)),
			catchError((_e: HttpErrorResponse) => of(this.emptyMap(ebeIds)))
		);
	}

  private emptyMap(ids: string[]): Map<string, string | null> {
    const m = new Map<string, string | null>();
    for (const id of ids) m.set(id, null);
    return m;
  }

  /**
   * Normalize backend responses into a Map of data URLs for mini avatars.
   * Primär erwartete Form:
   *   Array<WebBenutzerProfilbild> mit Feld "ebeProfilbildMini"
   * Fallbacks (zur Abwärtskompatibilität) werden weiterhin unterstützt.
   */
  private normalizeMiniResponse(payload: any): Map<string, string | null> {
    const mapOut = new Map<string, string | null>();
    const toDataUrl = (b64: string | null | undefined, mime?: string | null) =>
      base64ToDataUrl(b64 ?? null, mime || 'image/jpeg');

    if (!payload) return mapOut;

    // Bevorzugt: Liste von WebBenutzerProfilbild
    if (Array.isArray(payload)) {
      for (const it of payload) {
        const id = it?.ebeId ?? it?.id;
        const mini = it?.ebeProfilbildMini ?? it?.mini ?? it?.base64 ?? null;
        mapOut.set(id, toDataUrl(mini, it?.mime ?? null));
      }
      return mapOut;
    }

    // Fallback 1: { minis: { [ebeId]: base64|null }, mime?: string }
    if (payload && typeof payload === 'object' && payload.minis && typeof payload.minis === 'object') {
      const mime = payload.mime as (string | null | undefined);
      for (const [id, b64] of Object.entries(payload.minis)) {
        mapOut.set(id, toDataUrl(b64 as (string | null), mime));
      }
      return mapOut;
    }

    // Fallback 2: direktes Mapping { [ebeId]: base64|null }
    if (payload && typeof payload === 'object') {
      for (const [id, b64] of Object.entries(payload)) {
        if (typeof b64 === 'string' || b64 === null) {
          mapOut.set(id, toDataUrl(b64 as (string | null)));
        }
      }
      return mapOut;
    }

    return mapOut;
  }
}
